import {
  resolveEffectiveFundingPct,
  resolveEffectiveRevSharePct,
} from './resolvePlanFunding';
import { resolveCohortFundingMonth } from './computeDealIrrModel';
import { marginalFromCumulativeVector } from './roasSaturation';
import { resolveMonthlyFeePct } from './xirrFee';

const MAX_DISPLAY_MONTHS = 13; // M0..M12

/**
 * Returns the columns to render for the client cohort table.
 * Shows every month up to a maximum of 13 (M0..M12); when the cohort runs
 * longer, columns stop at M12.
 */
export function getClientCohortDisplayColumns(monthCount) {
  const count = Math.min(
    Math.max(0, Math.floor(Number(monthCount) || 0)),
    MAX_DISPLAY_MONTHS
  );
  return Array.from({ length: count }, (_, index) => ({
    header: `M${index}`,
    index,
    collapsed: false,
  }));
}

/** Picks the values for the given display columns out of a full-resolution row. */
export function selectClientCohortDisplayValues(values, columns) {
  return columns.map((column) => values[column.index]);
}

/**
 * Computes the Total column so it summarises only through the last displayed
 * column. When columns are not collapsed (or the tail is the true last month)
 * this matches the full-range totals; when the tail caps at M12 with more
 * months behind it, the totals stop at M12.
 */
export function computeClientCohortDisplayTotals(view, columns) {
  const lastIndex = columns.length
    ? columns[columns.length - 1].index
    : view.monthCount - 1;
  const sumThroughLastColumn = (values) => {
    let sum = 0;
    for (let index = 0; index <= lastIndex; index += 1) {
      sum += values[index] || 0;
    }
    return sum;
  };

  return {
    roasPct: sumThroughLastColumn(view.rows.roasPct),
    cohortRevenue: sumThroughLastColumn(view.rows.cohortRevenue),
    twelveFundsYou: sumThroughLastColumn(view.rows.twelveFundsYou),
    customerRepaysSupplier: sumThroughLastColumn(view.rows.customerRepaysSupplier),
    customerRepaysTwelve: sumThroughLastColumn(view.rows.customerRepaysTwelve),
    customerNetCash: sumThroughLastColumn(view.rows.customerNetCash),
    customerCashPosition: view.rows.customerCashPosition[lastIndex] ?? 0,
  };
}

/**
 * Computes return on equity using the Total-column values (capped at the last
 * displayed month), so the ROE only looks ahead as far as the table does.
 */
export function computeClientCohortDisplayEquity(view, displayTotals) {
  const marketingSpend = view.topKpis.cohortMarketingSpend;
  const twelveFunding = view.topKpis.twelveFunding;
  const totalRevenue = displayTotals.cohortRevenue;
  const totalRepaidTwelve = Math.abs(displayTotals.customerRepaysTwelve);

  const equityWithoutTwelve =
    marketingSpend > 0 ? totalRevenue / marketingSpend : null;
  const equityDenominator = marketingSpend - twelveFunding;
  const equityWithTwelve =
    equityDenominator > 0
      ? (totalRevenue - totalRepaidTwelve) / equityDenominator
      : null;

  return { equityWithoutTwelve, equityWithTwelve };
}

function resolveMarketingGrace(calcResult, state) {
  if (calcResult?.marketingGrace != null) {
    return calcResult.marketingGrace;
  }
  return Math.max(0, Math.min(1, Number(state.marketingGracePeriod ?? 1)));
}

function findFirstFundedCohortIndex(cohortDraws) {
  if (!cohortDraws?.length) {
    return -1;
  }
  return cohortDraws.findIndex((drawAmount) => drawAmount > 0);
}

function resolveLastRelativeMonthFromRows({
  cohortIndex,
  marginalRoas,
  cohortRevenue,
  cohortRepayments,
  monthCount,
}) {
  let lastRelativeMonth = marginalRoas.length - 1;
  for (let absoluteMonth = cohortIndex; absoluteMonth < monthCount; absoluteMonth += 1) {
    const relativeMonth = absoluteMonth - cohortIndex;
    const hasRevenue = (cohortRevenue[cohortIndex]?.[absoluteMonth] || 0) !== 0;
    const hasRepayment =
      (cohortRepayments[cohortIndex]?.[absoluteMonth] || 0) !== 0;
    if (hasRevenue || hasRepayment) {
      lastRelativeMonth = Math.max(lastRelativeMonth, relativeMonth);
    }
  }
  return lastRelativeMonth;
}

function buildClientCohortViewRows({
  cohortIndex,
  monthCount,
  marketingGrace,
  cohortMarketingSpend,
  twelveFunding,
  marginalRoas,
  cohortRevenue,
  cohortRepayments,
}) {
  const fundingRelativeMonth = Math.max(0, Math.floor(Number(marketingGrace) || 0));
  const roasPct = [];
  const cohortRevenueRow = [];
  const twelveFundsYou = [];
  const customerRepaysSupplier = [];
  const customerRepaysTwelve = [];
  const customerNetCash = [];
  const customerCashPosition = [];

  let cumulativeCash = 0;
  let totalCohortRevenue = 0;
  let totalRepaidTwelve = 0;

  for (let relativeMonth = 0; relativeMonth < monthCount; relativeMonth += 1) {
    const absoluteMonth = cohortIndex + relativeMonth;
    const revenue = cohortRevenue[cohortIndex]?.[absoluteMonth] || 0;
    const twelveFundAmount =
      relativeMonth === fundingRelativeMonth ? twelveFunding : 0;
    const supplierPayment =
      relativeMonth === fundingRelativeMonth ? -cohortMarketingSpend : 0;
    const repaidTwelve = -(cohortRepayments[cohortIndex]?.[absoluteMonth] || 0);
    const netCash = revenue + twelveFundAmount + supplierPayment + repaidTwelve;

    roasPct.push(marginalRoas[relativeMonth] ?? 0);
    cohortRevenueRow.push(revenue);
    twelveFundsYou.push(twelveFundAmount);
    customerRepaysSupplier.push(supplierPayment);
    customerRepaysTwelve.push(repaidTwelve);
    customerNetCash.push(netCash);

    cumulativeCash += netCash;
    customerCashPosition.push(cumulativeCash);

    totalCohortRevenue += revenue;
    totalRepaidTwelve += Math.abs(repaidTwelve);
  }

  const costOfFinancing = totalRepaidTwelve - twelveFunding;
  const costOfFinancingPct =
    twelveFunding > 0 ? (costOfFinancing / twelveFunding) * 100 : 0;

  const outOfPocketDay1 =
    monthCount > 1
      ? (cohortRevenueRow[1] || 0) +
        (twelveFundsYou[1] || 0) +
        (customerRepaysSupplier[1] || 0)
      : 0;

  const equityWithoutTwelve =
    cohortMarketingSpend > 0 ? totalCohortRevenue / cohortMarketingSpend : null;
  const equityDenominator = cohortMarketingSpend - twelveFunding;
  const equityWithTwelve =
    equityDenominator > 0
      ? (totalCohortRevenue - totalRepaidTwelve) / equityDenominator
      : null;

  return {
    rows: {
      roasPct,
      cohortRevenue: cohortRevenueRow,
      twelveFundsYou,
      customerRepaysSupplier,
      customerRepaysTwelve,
      customerNetCash,
      customerCashPosition,
    },
    totals: {
      roasPct: marginalRoas.reduce((sum, value) => sum + value, 0),
      cohortRevenue: totalCohortRevenue,
      twelveFundsYou: twelveFunding,
      customerRepaysSupplier: -cohortMarketingSpend,
      customerRepaysTwelve: -totalRepaidTwelve,
      customerNetCash: customerNetCash.reduce((sum, value) => sum + value, 0),
      customerCashPosition:
        customerCashPosition[customerCashPosition.length - 1] ?? 0,
    },
    bottomKpis: {
      costOfFinancing,
      costOfFinancingPct,
      outOfPocketDay1,
      equityWithoutTwelve,
      equityWithTwelve,
    },
  };
}

export function buildClientCohortView(calcResult, state) {
  const cohortIndex = findFirstFundedCohortIndex(calcResult.cohortDraws);
  if (cohortIndex < 0) {
    return null;
  }

  const marketingGrace = resolveMarketingGrace(calcResult, state);
  const vectorPct =
    calcResult.effectiveRepaymentRoasVectors?.[cohortIndex] ??
    calcResult.spendRoasVectors?.[cohortIndex] ??
    state.roas ??
    [];
  const marginalRoas = marginalFromCumulativeVector(vectorPct);
  const cohortMarketingSpend = state.marketing[cohortIndex] || 0;
  const twelveFunding = calcResult.cohortDraws[cohortIndex] || 0;
  const twelveFundingPct = resolveEffectiveFundingPct(state, cohortIndex);
  const twelveRevSharePct = resolveEffectiveRevSharePct(state, cohortIndex);
  const cappedReturnPct = resolveMonthlyFeePct(state.feeType, state.feePct);

  const lastRelativeMonth = resolveLastRelativeMonthFromRows({
    cohortIndex,
    marginalRoas,
    cohortRevenue: calcResult.cohortRevenue,
    cohortRepayments: calcResult.cohortRepayments,
    monthCount: calcResult.N_OUT,
  });
  const monthCount = lastRelativeMonth + 1;

  const built = buildClientCohortViewRows({
    cohortIndex,
    monthCount,
    marketingGrace,
    cohortMarketingSpend,
    twelveFunding,
    marginalRoas,
    cohortRevenue: calcResult.cohortRevenue,
    cohortRepayments: calcResult.cohortRepayments,
  });

  return {
    cohortIndex,
    cols: Array.from({ length: monthCount }, (_, relativeMonth) => `M${relativeMonth}`),
    monthCount,
    marketingGrace,
    topKpis: {
      cohortMarketingSpend,
      twelveFunding,
      twelveFundingPct,
      twelveRevSharePct,
      cappedReturnPct,
    },
    rows: built.rows,
    totals: built.totals,
    bottomKpis: built.bottomKpis,
  };
}

function resolveDealIrrMarginalRoasPct(dealIrrModel, cohortIndex) {
  const mobCount = dealIrrModel.incremental?.length || 0;
  return Array.from({ length: mobCount }, (_, mob) => {
    let roas = Number(dealIrrModel.incremental[mob]) || 0;
    if (dealIrrModel.mode === 'ltv') {
      roas *= Number(dealIrrModel.saturation?.[cohortIndex]) || 0;
      roas *= Number(dealIrrModel.learningByOrigin?.[cohortIndex]) || 1;
    }
    return roas * 100;
  });
}

export function buildClientCohortViewFromDealIrrModel(dealIrrModel, state, inputs) {
  if (!dealIrrModel) {
    return null;
  }

  const marketingGrace = Math.max(
    0,
    Math.min(1, Number(inputs?.marketingGrace ?? state?.marketingGracePeriod ?? 1))
  );
  let cohortIndex = -1;
  let fundingMonth = -1;
  for (let origin = 0; origin < dealIrrModel.originCount; origin += 1) {
    const month = resolveCohortFundingMonth(origin, marketingGrace);
    const draw = Number(dealIrrModel.customerFunding[month]) || 0;
    if (draw > 0) {
      cohortIndex = origin;
      fundingMonth = month;
      break;
    }
  }
  if (cohortIndex < 0) {
    return null;
  }

  const cohortMarketingSpend = dealIrrModel.marketing[cohortIndex] || 0;
  const twelveFunding = dealIrrModel.customerFunding[fundingMonth] || 0;
  const twelveFundingPct = Math.round(
    (Number(dealIrrModel.twelveFundingPct[fundingMonth]) || 0) * 100
  );
  const twelveRevSharePct = resolveEffectiveRevSharePct(state, cohortIndex);
  const cappedReturnPct = resolveMonthlyFeePct(state.feeType, state.feePct);
  const marginalRoas = resolveDealIrrMarginalRoasPct(dealIrrModel, cohortIndex);

  const lastRelativeMonth = resolveLastRelativeMonthFromRows({
    cohortIndex,
    marginalRoas,
    cohortRevenue: dealIrrModel.cohortGrossRevenue,
    cohortRepayments: dealIrrModel.cohortRepaymentByOrigin,
    monthCount: dealIrrModel.monthCount,
  });
  const monthCount = lastRelativeMonth + 1;

  const built = buildClientCohortViewRows({
    cohortIndex,
    monthCount,
    marketingGrace,
    cohortMarketingSpend,
    twelveFunding,
    marginalRoas,
    cohortRevenue: dealIrrModel.cohortGrossRevenue,
    cohortRepayments: dealIrrModel.cohortRepaymentByOrigin,
  });

  return {
    cohortIndex,
    cols: Array.from({ length: monthCount }, (_, relativeMonth) => `M${relativeMonth}`),
    monthCount,
    marketingGrace,
    topKpis: {
      cohortMarketingSpend,
      twelveFunding,
      twelveFundingPct,
      twelveRevSharePct,
      cappedReturnPct,
    },
    rows: built.rows,
    totals: built.totals,
    bottomKpis: built.bottomKpis,
  };
}
